import React, { useState, useCallback } from 'react';
import { Box } from '@mui/material';
import { useCANvisionContext } from '../../context/useCANvisionContext';
import { useAttentionFeed } from '../../hooks/useAttentionFeed';
import { DashboardHeader } from './DashboardHeader';
import { ControlPanel } from './ControlPanel';
import { StatsCards } from './StatsCards';
import { AttentionFeed } from './AttentionFeed';
import { EventsIncidentsTabs } from './EventsIncidentsTabs';
import { DashboardDrawers } from './DashboardDrawers';
import { ComponentErrorBoundary } from '../ComponentErrorBoundary';
import CameraDetail from '../CameraDetail/CameraDetail';
import { getStreamUrl } from 'api/canvms';
import type { AttentionCamera, VMSCamera, StreamInfo } from '../../types/canvision.types';

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

/**
 * Main CANvision Dashboard layout container.
 *
 * Composes the dashboard shell from the following sections:
 *  1. DashboardHeader  — title + LIVE indicator
 *  2. ControlPanel (left) + StatsCards (right) in a responsive row
 *  3. AttentionFeed       — live high-severity camera streams
 *  4. EventsIncidentsTabs — Events / Incidents tabbed section
 *  5. DashboardDrawers    — all management drawers (site, camera, etc.)
 *
 * Data is sourced from the shared `CANvisionProvider` context and the
 * `useAttentionFeed` hook (which subscribes to WebSocket events for
 * high-severity camera feed updates).
 */
export function CANvisionDashboard() {
  const {
    systemHealth,
    socketConnection,
    dashboardStats,
    dashboardStatsLoading,
    cameras: contextCameras,
  } = useCANvisionContext();

  // Attention Feed — subscribes to WebSocket events for high-severity cameras
  const feed = useAttentionFeed();

  // -- CameraDetail modal state -----------------------------------------------
  const [cameraDetailOpen, setCameraDetailOpen] = useState(false);
  const [selectedCamera, setSelectedCamera] = useState<AttentionCamera | null>(null);
  const [cameraDetailStreamInfo, setCameraDetailStreamInfo] = useState<StreamInfo | null>(null);
  /** The full VMSCamera object from context (for ptz_capable, display_name, etc.) */
  const [fullCameraObj, setFullCameraObj] = useState<VMSCamera | null>(null);

  /**
   * Opens the CameraDetail modal when a tile in the Attention Feed is clicked.
   * Looks up the full camera object from context cameras (to get ptz_capable,
   * display_name, etc.) and fetches the stream URL for the modal player.
   */
  const handleCameraClick = useCallback(
    async (camera: AttentionCamera) => {
      setSelectedCamera(camera);
      setCameraDetailOpen(true);

      // Look up the full camera object from context cameras
      const match = contextCameras.find((c) => c.id === camera.cameraId) ?? null;
      setFullCameraObj(match);

      // The attention camera already has a stream URL from enrichment,
      // but CameraDetail needs the full StreamInfo (stream_url + snapshot_url + type).
      // Fetch it fresh for the modal.
      try {
        const response = await getStreamUrl(camera.siteId, camera.cameraId);
        setCameraDetailStreamInfo(response.data);
      } catch (err) {
        console.error('[CANvision/Dashboard] Failed to fetch stream info for CameraDetail:', err);
        // If stream fetch fails, still open the modal — it will show without video
        setCameraDetailStreamInfo(null);
      }
    },
    [contextCameras],
  );

  /** Close the CameraDetail modal and reset state. */
  const handleCameraDetailClose = useCallback(() => {
    setCameraDetailOpen(false);
    setSelectedCamera(null);
    setCameraDetailStreamInfo(null);
    setFullCameraObj(null);
  }, []);

  /**
   * Called by CameraDetail when an event is successfully acknowledged,
   * dismissed, or promoted.  Removes the event from the Attention Feed
   * so the tile disappears (or recalculates) immediately rather than
   * waiting for the WebSocket echo.
   */
  const handleEventRemoved = useCallback(
    (eventId: number) => {
      if (selectedCamera) {
        feed.dismissEvent(selectedCamera.cameraId, eventId);
      }
    },
    [selectedCamera, feed],
  );

  // Derive a simple ServiceStatus from the HealthStatus object
  const systemStatus = systemHealth?.status === 'healthy'
    ? 'up' as const
    : systemHealth?.status === 'critical'
      ? 'down' as const
      : 'unknown' as const;

  // Build a camera-like object for CameraDetail from the full VMSCamera or
  // fall back to the AttentionCamera fields when the camera is on a different site.
  const cameraForDetail = fullCameraObj ?? (selectedCamera
    ? {
        id: selectedCamera.cameraId,
        display_name: selectedCamera.cameraName,
        name: selectedCamera.cameraName,
        ptz_capable: false,
      }
    : null);

  return (
    <Box sx={{ p: { xs: 1, sm: 2 } }}>
      {/* Title bar — outside Paper */}
      <DashboardHeader
        systemStatus={systemStatus}
        socketConnected={socketConnection.isConnected}
      />

      {/* Top row: Control Panel (left) + Stats Cards (right) */}
      <Box
        sx={{
          display: 'flex',
          gap: 2,
          flexDirection: { xs: 'column', md: 'row' },
          mt: 2,
        }}
      >
        <ControlPanel sx={{ flex: { xs: '1 1 100%', md: '2 1 0' } }} />
        <StatsCards
          stats={dashboardStats}
          loading={dashboardStatsLoading}
          sx={{ flex: { xs: '1 1 100%', md: '3 1 0' } }}
        />
      </Box>

      {/* Attention Feed — live high-severity camera streams */}
      <Box sx={{ mt: 2 }}>
        <ComponentErrorBoundary sectionName="Attention Feed">
          <AttentionFeed
            feed={feed}
            loading={false}
            onCameraClick={handleCameraClick}
          />
        </ComponentErrorBoundary>
      </Box>

      {/* Events / Incidents Tabs */}
      <Box sx={{ mt: 2 }}>
        <ComponentErrorBoundary sectionName="Events & Incidents">
          <EventsIncidentsTabs />
        </ComponentErrorBoundary>
      </Box>

      {/* Management Drawers (Site, Camera Settings, Camera Sets, Checklist) */}
      <DashboardDrawers />

      {/* CameraDetail modal — full-screen camera viewer with live/playback */}
      {selectedCamera && (
        <CameraDetail
          open={cameraDetailOpen}
          onClose={handleCameraDetailClose}
          camera={cameraForDetail}
          siteId={selectedCamera.siteId}
          siteName={selectedCamera.siteName}
          liveStreamUrl={cameraDetailStreamInfo?.stream_url || selectedCamera.streamUrl}
          snapshotUrl={cameraDetailStreamInfo?.snapshot_url}
          streamType={cameraDetailStreamInfo?.type || selectedCamera.streamType || 'hls'}
          events={selectedCamera.events}
          onEventsEmpty={handleCameraDetailClose}
          onEventRemoved={handleEventRemoved}
        />
      )}
    </Box>
  );
}

export default CANvisionDashboard;
